import EditIcon from '@mui/icons-material/Edit';
import { Grid, Box } from '@mui/material';
import { styled } from '@mui/styles';
import { isEmpty, omit } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  getJobFieldSettings,
  IJobFieldSettings,
  IJobFieldSettingsType,
  saveJobFieldSettings,
} from 'wisestep-api';
import { WsTypography, WsTypoVariants, Label, WsButton } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../components/HeaderBox';
import JobSettingsLayout from '../components/JobSettingsLayout';

import CustomizeDefCurrencyDialog from './CustomizeDefCurrencyDialog';
import CustomizeHiringForDialog from './CustomizeHiringForDialog';
import CustomizeJobIndustryDialog from './CustomizeJobIndustryDialog';
import CustomizeJobStatusDialog from './CustomizeJobStatusDialog';
import CustomizeJobTypeDialog from './CustomizeJobTypeDialog';
import CustomizeWorkplacePreferenceDialog from './CustomizeWorkplacePreferenceDialog';
import { getDefaultJobFieldSettings } from './Helper';

const StyledLabel = styled(Label)(() => ({
  fontWeight: 'bold',
  width: '13vw',
  display: 'flex',
  justifyContent: 'space-between',
  paddingRight: '2vw',
}));
type fieldsArrayType =
  | 'title'
  | 'jobCode'
  | 'locations'
  | 'description'
  | 'compensation'
  | 'clientBillRate'
  | 'clientBillRateNew'
  | 'payRateExpenses'
  | 'jobType'
  | 'jobStatusType'
  | 'remoteJob'
  | 'workplacePreference'
  | 'skills'
  | 'companyName'
  | 'clientRecruiterName'
  | 'tags'
  | 'clientJobCode'
  | 'numberOfPositions'
  | 'accountManagerName'
  | 'insight'
  | 'jobIndustryName'
  | 'jobCategory'
  | 'jobOccupation'
  | 'workAuthorization'
  | 'url'
  | 'otherBenefitDetails'
  | 'jobSource'
  | 'visaRequirements'
  | 'qualifications'
  | 'educationRequirements'
  | 'experienceRequirements'
  | 'taxTerms'
  | 'hiringFor'
  | 'workDuration'
  | 'defaultCurrency'
  | 'feeArrangement'
  | 'submissionFormTemplete';

const CustomizeJobFields = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspaceContext = useWorkspaceContext();
  const [dialogControlTwo, setDialogControlTwo] = useState(false);
  const [controlThree, setControlThree] = useState(false);
  const [showDialog, setshowDialog] = useState(false);
  const [openWorkPreferenceDialog, setOpenWorkPreferenceDialog] = useState<boolean>(false);
  const [openHiringForDialogBox, setOpenHiringForDialogBox] = useState(false);
  const [openJobIndustryDialog, setOpenJobIndustryDialog] = useState(false);

  const openDialog = () => setshowDialog(true);
  const closeDialog = () => setshowDialog(false);

  const dialogControlTwoOpen = () => setDialogControlTwo(true);
  const dialogControlTwoClose = () => setDialogControlTwo(false);

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_JOB_FIELDS_CUSTOMISATION,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const [fieldsArray, setFieldsArray] = useState<fieldsArrayType[]>([]);
  const [jobFieldSettingsData, setJobFieldSettingsData] = useState<IJobFieldSettings>(
    getDefaultJobFieldSettings()
  );
  const controlHiringForDialogBox = (isOpen: boolean) => setOpenHiringForDialogBox(isOpen);

  const jobIndustryOpen = () => setOpenJobIndustryDialog(true);
  const jobIndustryClose = () => setOpenJobIndustryDialog(false);

  const [modifiedData, setModifiedData] = useState<
    Omit<
      IJobFieldSettings,
      'id' | 'workspaceId' | 'createdBy' | 'createdOn' | 'modifiedBy' | 'modifiedOn'
    >
  >(getDefaultJobFieldSettings());

  const navigate = useNavigate();

  const onChangeHandler = (data: IJobFieldSettingsType, index: number) => {
    if (jobFieldSettingsData) {
      const tempData = jobFieldSettingsData;
      switch (index) {
        case 0:
          tempData.jobType = data;
          break;
        case 1:
          tempData.jobStatusType = data;
          break;
        case 2:
          tempData.hiringFor = data;
          break;
        case 3:
          tempData.defaultCurrency = data;
          break;
        case 4:
          tempData.salaryTimeSpan = data;
          break;
        case 5:
          tempData.jobIndustryName = data;
          break;
        case 6:
          tempData.workplacePreference = data;
          break;
      }
      setJobFieldSettingsData({ ...tempData });
    }
  };
  const formDefaultValues = modifiedData;
  const formOptions = {
    defaultValues: formDefaultValues,
  };
  const methodsMain = useForm(formOptions);
  const { handleSubmit, setValue, control, reset } = methodsMain;
  useEffect(() => {
    (async function () {
      const getJobFieldSettingsReq = await getJobFieldSettings();
      if (!isEmpty(getJobFieldSettingsReq.data)) {
        if (getJobFieldSettingsReq.data.id) {
          const fieldsdata = Object.fromEntries(
            Object.entries(getJobFieldSettingsReq.data).map(([key, value]) => [
              key,
              value ?? getDefaultJobFieldSettings()[key],
            ])
          );
          setJobFieldSettingsData(fieldsdata as IJobFieldSettings);
        }
      } else {
        await saveJobFieldSettings(jobFieldSettingsData);
      }
    })();
  }, []);

  useEffect(() => {
    const payload = omit(jobFieldSettingsData, [
      'id',
      'workspaceId',
      'createdBy',
      'createdOn',
      'modifiedBy',
      'modifiedOn',
    ]);
    if (jobFieldSettingsData?.id) setModifiedData({ ...payload });
    else setModifiedData(getDefaultJobFieldSettings());
    const configuredData = Object.entries(payload).map((entry) => entry[0] as fieldsArrayType);
    reset(payload);
    setFieldsArray(configuredData);
  }, [jobFieldSettingsData, reset]);

  const onSaveHandler = async (formData: FieldValues) => {
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }

    if (jobFieldSettingsData) {
      try {
        const tempData = { ...formData };
        if (jobFieldSettingsData?.id) {
          tempData.id = jobFieldSettingsData.id;
          tempData.workspaceID = jobFieldSettingsData.workspaceId;
          tempData.createdBy = jobFieldSettingsData.createdBy;
          tempData.createdOn = jobFieldSettingsData.createdOn;
          tempData.modifiedBy = jobFieldSettingsData.modifiedBy;
          tempData.modifiedOn = jobFieldSettingsData.modifiedOn;
        }

        await saveJobFieldSettings(tempData as IJobFieldSettings);
        setNotification(success('Configure Job Attributes,Settings updated successfully'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  const updaterFunction = (fieldName, boolValue: boolean, props, index: number) => {
    if (jobFieldSettingsData) {
      const tempData = props.field.value;
      const changedData = jobFieldSettingsData;
      switch (index) {
        case 0:
          tempData.mandatory = boolValue;
          break;
        case 1:
          tempData.hide = boolValue;
          break;
      }
      setValue(fieldName, { ...tempData });
      changedData[fieldName] = { ...tempData };
      setJobFieldSettingsData({ ...changedData });
    }
  };

  const editableFields = [
    'Job Status',
    'Job Type',
    'Workplace Preference',
    'Hiring For',
    'Job Industry',
    'Job Category',
  ];

  const handleEditClick = (label: string) => {
    switch (label) {
      case 'Workplace Preference':
        setOpenWorkPreferenceDialog(true);
        break;
      case 'Job Category':
        navigate('/job-category-configuration');
        break;
      case 'Job Industry':
        jobIndustryOpen();
        break;
      case 'Hiring For':
        controlHiringForDialogBox(true);
        break;
      case 'Job Status':
        dialogControlTwoOpen();
        break;
      case 'Job Type':
        openDialog();
        break;
      default:
    }
  };
  return (
    <JobSettingsLayout
      title="Job Attribute Settings"
      indexLabel="Customize Job Fields"
      indexUrl="/job-fields-settings"
      breadCrumbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.JOBS,
        Pages['JOB_ATTRIBUTE_SETTING']
      )}
    >
      <Box component="form" px={8} pt={2}>
        <FormProvider {...methodsMain}>
          <form>
            <HeaderBox mb={4}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE}>Attributes</WsTypography>
              <WsButton type="submit" onClick={handleSubmit(onSaveHandler)} size="small">
                Save
              </WsButton>
            </HeaderBox>

            <Grid container direction="column" rowSpacing={1} mb={2}>
              {modifiedData &&
                fieldsArray?.map((fieldName: fieldsArrayType) => (
                  <Grid key={fieldName} item display="flex" justifyContent="space-between">
                    {fieldName != 'defaultCurrency' && fieldName != 'remoteJob' && (
                      <>
                        <Controller
                          name={fieldName}
                          control={control}
                          render={(props) => (
                            <>
                              <StyledLabel>
                                {props.field.value.label}
                                {editableFields.includes(props.field.value.label) && (
                                  <WsButton
                                    size="small"
                                    fullWidth={false}
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditClick(props.field.value.label)}
                                  >
                                    Edit
                                  </WsButton>
                                )}
                              </StyledLabel>
                              <WsButton
                                variant={
                                  props.field.value.mandatory === true ? 'contained' : 'outlined'
                                }
                                disabled={!manageAccess || props.field.value.disabled}
                                onClick={() => {
                                  updaterFunction(fieldName, true, props, 0);
                                  updaterFunction(fieldName, false, props, 1);
                                }}
                              >
                                Mandatory
                              </WsButton>
                              <WsButton
                                variant={
                                  props.field.value.mandatory === false ? 'contained' : 'outlined'
                                }
                                disabled={!manageAccess || props.field.value.disabled}
                                onClick={() => updaterFunction(fieldName, false, props, 0)}
                              >
                                Non-Mandatory
                              </WsButton>
                            </>
                          )}
                        />
                        <Controller
                          name={fieldName}
                          control={control}
                          render={(props) => (
                            <>
                              <WsButton
                                variant={
                                  props.field.value.hide === false ? 'contained' : 'outlined'
                                }
                                disabled={
                                  !manageAccess || props.field.value.mandatory
                                    ? true
                                    : props.field.value.disabled
                                }
                                onClick={() => updaterFunction(fieldName, false, props, 1)}
                              >
                                Show
                              </WsButton>
                              <WsButton
                                variant={props.field.value.hide === true ? 'contained' : 'outlined'}
                                disabled={
                                  !manageAccess || props.field.value.mandatory
                                    ? true
                                    : props.field.value.disabled
                                }
                                onClick={() => updaterFunction(fieldName, true, props, 1)}
                              >
                                Hide
                              </WsButton>
                            </>
                          )}
                        />
                      </>
                    )}
                    {fieldName === 'defaultCurrency' && (
                      <Controller
                        name={fieldName}
                        control={control}
                        render={(props) => (
                          <>
                            <StyledLabel>{props.field.value.label}</StyledLabel>
                            <WsButton
                              size="small"
                              fullWidth={false}
                              startIcon={<EditIcon />}
                              onClick={() => setControlThree(true)}
                            >
                              Edit
                            </WsButton>
                          </>
                        )}
                      />
                    )}
                  </Grid>
                ))}
            </Grid>

            <CustomizeJobTypeDialog
              openDialog={showDialog}
              closeDialog={() => closeDialog()}
              onChangeHandler={(data) => onChangeHandler(data, 0)}
              modifiedData={modifiedData}
            />
            <CustomizeJobStatusDialog
              openDialog={dialogControlTwo}
              closeDialog={() => dialogControlTwoClose()}
              onChangeHandler={(data) => onChangeHandler(data, 1)}
              modifiedData={modifiedData}
            />
            <CustomizeHiringForDialog
              openDialog={openHiringForDialogBox}
              closeDialog={() => controlHiringForDialogBox(false)}
              onChangeHandler={(data) => onChangeHandler(data, 2)}
              modifiedData={modifiedData}
            />
            <CustomizeDefCurrencyDialog
              openDialog={controlThree}
              closeDialog={() => setControlThree(false)}
              onChangeHandler={onChangeHandler}
              modifiedData={modifiedData}
            />
            <CustomizeJobIndustryDialog
              openDialog={openJobIndustryDialog}
              closeDialog={() => jobIndustryClose()}
              onChangeHandler={(data) => onChangeHandler(data, 5)}
              modifiedData={modifiedData}
            />
            {openWorkPreferenceDialog && (
              <CustomizeWorkplacePreferenceDialog
                openDialog={openWorkPreferenceDialog}
                closeDialog={() => setOpenWorkPreferenceDialog(false)}
                onChangeHandler={(data) => onChangeHandler(data, 6)}
                modifiedData={modifiedData}
              />
            )}
          </form>
        </FormProvider>
      </Box>
    </JobSettingsLayout>
  );
};

export default CustomizeJobFields;
